import type { PlayerColor } from "@ludo/shared";

/**
 * Standard 52-cell Ludo main track on a 15x15 grid (row, col), clockwise.
 * Index 0 is RED start. The same mapping is used by the web client renderer.
 */
export const TRACK_CELLS: ReadonlyArray<readonly [number, number]> = [
  // row 6, left -> right
  [6, 0], [6, 1], [6, 2], [6, 3], [6, 4], [6, 5],
  // column 6 up
  [5, 6], [4, 6], [3, 6], [2, 6], [1, 6], [0, 6],
  // top arm
  [0, 7], [0, 8],
  // column 8 down
  [1, 8], [2, 8], [3, 8], [4, 8], [5, 8],
  // row 6 right side
  [6, 9], [6, 10], [6, 11], [6, 12], [6, 13], [6, 14],
  // right arm
  [7, 14], [8, 14],
  // column 8 lower, right -> left
  [8, 13], [8, 12], [8, 11], [8, 10], [8, 9],
  // row 8 down side
  [9, 8], [10, 8], [11, 8], [12, 8], [13, 8], [14, 8],
  // bottom arm
  [14, 7], [14, 6],
  // column 6 lower, down -> up
  [13, 6], [12, 6], [11, 6], [10, 6], [9, 6],
  // row 8 left side
  [8, 5], [8, 4], [8, 3], [8, 2], [8, 1], [8, 0],
  // entry to red home column
  [7, 0],
];

export const TRACK_LENGTH = 52;

/** First track index for each color (engine + UI share this). */
export const COLOR_START_INDEX: Record<PlayerColor, number> = {
  RED: 0,
  GREEN: 13,
  YELLOW: 26,
  BLUE: 39,
};

/** Home column grid cells for progress 51..55 per color. */
export const HOME_COLUMNS: Record<PlayerColor, ReadonlyArray<readonly [number, number]>> = {
  RED: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5]],
  GREEN: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]],
  YELLOW: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9]],
  BLUE: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7]],
};

/** Yard (home base) piece spots inside each 6x6 quadrant. */
export const YARD_CELLS: Record<PlayerColor, ReadonlyArray<readonly [number, number]>> = {
  RED: [[1, 1], [1, 3], [3, 1], [3, 3]],
  GREEN: [[1, 11], [1, 13], [3, 11], [3, 13]],
  YELLOW: [[11, 11], [11, 13], [13, 11], [13, 13]],
  BLUE: [[11, 1], [11, 3], [13, 1], [13, 3]],
};

/** Safe track indices: 4 start cells + 4 star cells. */
export const SAFE_TRACK_INDICES: ReadonlySet<number> = new Set([0, 8, 13, 21, 26, 34, 39, 47]);

/** Progress values. */
export const YARD = -1;
export const HOME_COLUMN_START = 51;
export const FINISH_PROGRESS = 56;

/** Absolute track index (0..51) for a color at a given progress, or null if off-track. */
export function absoluteTrackIndex(color: PlayerColor, progress: number): number | null {
  if (progress < 0 || progress > 50) return null;
  return (COLOR_START_INDEX[color] + progress) % TRACK_LENGTH;
}

export function isSafeCell(trackIndex: number): boolean {
  return SAFE_TRACK_INDICES.has(trackIndex);
}
